import * as React from "react";
import { Project, ProjectLineItem } from "@interfaces/project";
import { ProfileLineItem } from "@interfaces/profile";
import { decodeProject, encodeProject } from "decoders/project";
import { getApiData, listApiData, postApiData } from "@hooks/utils/api";

export const useProject = (
  userProfile?: ProfileLineItem,
  projectId?: string
) => {
  const [selectedProject, setSelectedProject] =
    React.useState<ProjectLineItem | null>(null);
  const [projects, setProjects] = React.useState<ProjectLineItem[]>([]);
  const [isProjectLoading, setIsProjectLoading] =
    React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const getProject = async (id: string): Promise<void> => {
    try {
      setIsProjectLoading(true);
      const project = (await getApiData("getProject", "project", id))
        .data as Project;
      if (project) {
        const projectLineItem = await decodeProject(project);
        setSelectedProject(projectLineItem);
      } else {
        setSelectedProject(null);
      }
    } catch (e: any) {
      setError("Could not fetch project");
    } finally {
      setIsProjectLoading(false);
    }
  };

  const listProjects = async (organisationId: string): Promise<void> => {
    try {
      setIsProjectLoading(true);

      const projectRes = await listApiData(
        "listProjects",
        "project",
        organisationId
      );
      const projectLineItems = await Promise.all(
        projectRes.data.map((p) => decodeProject(p as Project))
      );
      setProjects(projectLineItems);
    } catch (e: any) {
      setError("Could not list projects");
    } finally {
      setIsProjectLoading(false);
    }
  };

  const createProject = async (project: ProjectLineItem): Promise<void> => {
    try {
      setIsProjectLoading(true);
      (await postApiData("createProject", "project", encodeProject(project)))
        .data as Project;
      setSelectedProject(project);
    } catch (e: any) {
      setError("Could not list projects");
    } finally {
      setIsProjectLoading(false);
    }
  };

  const updateProject = async (project: ProjectLineItem): Promise<void> => {
    try {
      setIsProjectLoading(true);
      (await postApiData("updateProject", "project", encodeProject(project)))
        .data as Project;
      setSelectedProject(project);
    } catch (e: any) {
      setError("Could not update project");
    } finally {
      setIsProjectLoading(false);
    }
  };

  const deleteProject = async (project: ProjectLineItem): Promise<void> => {
    try {
      setIsProjectLoading(true);
      (await postApiData("deleteProject", "project", encodeProject(project)))
        .data as Project;
      setProjects(projects.filter((p) => p.id !== project.id));
    } catch (e: any) {
      setError("Could not delete project");
    } finally {
      setIsProjectLoading(false);
    }
  };

  React.useEffect(() => {
    if (userProfile) {
      listProjects(userProfile.organisationId);
    }
  }, [userProfile]);

  React.useEffect(() => {
    if (projectId) {
      getProject(projectId);
    }
  }, [projectId]);

  return {
    selectedProject,
    projects,
    setSelectedProject,
    getProject,
    listProjects,
    createProject,
    updateProject,
    deleteProject,
    isProjectLoading,
    error,
  };
};
